import { animate, type MotionValue, useMotionValue, useMotionValueEvent, useReducedMotion } from 'motion/react'
import {
  cloneElement,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { cn } from '../lib/utils'

// Gooey Popover — a click/hover popover whose panel "oozes" out of the trigger through an
// SVG goo filter (a metaball merge), with the crisp content fading in on top. `motion/react`
// only, no Radix/Floating UI. Named GooeyPopover* to sit alongside the Radix-based `Popover`.
//
// Adapted from beUI's Gooey Popover (https://beui.dev/components/motion/popover), MIT-licensed
// (© beUI, https://github.com/starc007/ui-components). Local changes: Parsley tokens/naming,
// added focus management (focus-in on open, return-to-trigger on close), aria-label passthrough,
// and a calmer default gooStrength.
//
// Use it for interactive, click-summoned popovers (e.g. the header undo control). It is NOT
// a hover-hint tooltip — for those keep the Radix `Tooltip`.
//
// Positioning is manual (`side`/`align`/`sideOffset`), with a lightweight viewport SHIFT on the
// horizontal (align) axis: a panel that would overflow a screen edge is nudged back into view —
// and pinned to the near edge if it's wider than the space (#1033, first hit by the wide plan-gen
// popover on iOS / narrow screens). The trigger stays put; only the panel target moves, so the
// goo blob simply travels to meet it. Vertical flip (a `side="top"` panel overflowing upward) is
// still not handled — layer Floating UI if a future consumer needs it. See #948 for the origin.

type Side = 'top' | 'bottom'
type Align = 'start' | 'center' | 'end'
type TriggerMode = 'click' | 'hover'

const GOO_SPRING = { type: 'spring', visualDuration: 0.32, bounce: 0.28 } as const
const HOVER_CLOSE_DELAY = 120
// Min gap the shifted panel keeps from each viewport edge, in px.
const VIEWPORT_PADDING = 8

// Elevation for the open panel — a plain `box-shadow` on a rounded rect at the panel, NOT a
// filter `drop-shadow()`. iOS/WebKit clips a drop-shadow on a filtered + opacity-faded element
// to that element's box (the shadow gets cut off in a rectangle); box-shadow isn't subject to
// that. The panel is a rounded rectangle at full-open (there's no persistent neck), so a
// box-shadow fits. This is the app's --shadow-peek (brand-green tinted, faint on dark).
const PANEL_SHADOW = '0 2px 6px rgb(30 51 25 / 0.08), 0 16px 34px -14px rgb(30 51 25 / 0.28)'

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

// The panel's base x-offset from the trigger's left edge, before any viewport shift.
function alignOffsetX(align: Align, tW: number, cW: number): number {
  return align === 'start' ? 0 : align === 'end' ? tW - cW : (tW - cW) / 2
}

// Horizontal shift (px) that keeps the panel inside the viewport. Given the trigger's
// viewport-left, the panel's base offset from it, the panel width, and the viewport width,
// returns how far to nudge the panel along the trigger's edge so it clears both screen edges
// by `pad` (#1033). Returns 0 when the panel already fits (the common case). If the panel is
// wider than the available space it's pinned to the left edge — keeping its start visible —
// rather than centered half off-screen.
function clampShiftX(triggerLeft: number, basePx: number, panelW: number, viewportW: number, pad: number): number {
  const panelLeft = triggerLeft + basePx
  const minLeft = pad
  const maxLeft = viewportW - pad - panelW
  const clampedLeft = maxLeft < minLeft ? minLeft : Math.min(Math.max(panelLeft, minLeft), maxLeft)
  return clampedLeft - panelLeft
}

interface Rect {
  x: number
  y: number
  w: number
  h: number
  r: number
}
interface Geo {
  layerW: number
  layerH: number
  left: number
  top: number
  trigger: Rect
  panel: Rect
}

// Trigger rect and panel rect in a shared local coordinate box. `shiftX` nudges the panel along
// the trigger's edge to keep it inside the viewport (see clampShiftX) — the trigger is unchanged.
function buildGeo(
  tW: number,
  tH: number,
  cW: number,
  cH: number,
  side: Side,
  align: Align,
  gap: number,
  panelRadius: number,
  shiftX = 0
): Geo {
  const py = side === 'bottom' ? tH + gap : -(gap + cH)
  const px = alignOffsetX(align, tW, cW) + shiftX

  const left = Math.min(0, px)
  const top = Math.min(0, py)
  const layerW = Math.max(tW, px + cW) - left
  const layerH = Math.max(tH, py + cH) - top

  const triggerRadius = Math.min(tH / 2, panelRadius)

  return {
    layerW,
    layerH,
    left,
    top,
    trigger: { x: -left, y: -top, w: tW, h: tH, r: triggerRadius },
    panel: { x: px - left, y: py - top, w: cW, h: cH, r: panelRadius }
  }
}

function insetFor(rect: Rect, layerW: number, layerH: number): string {
  const top = rect.y
  const right = layerW - (rect.x + rect.w)
  const bottom = layerH - (rect.y + rect.h)
  const left = rect.x
  return `inset(${top}px ${right}px ${bottom}px ${left}px round ${rect.r}px)`
}

// The morphing rect at progress `p` — trigger shape at 0, panel shape at 1. One definition of
// the ooze geometry, read by both the clip path and the shadow so they can't fall out of step.
function rectForProgress(geo: Geo, p: number): Rect {
  const t = geo.trigger
  const pn = geo.panel
  return {
    x: lerp(t.x, pn.x, p),
    y: lerp(t.y, pn.y, p),
    w: lerp(t.w, pn.w, p),
    h: lerp(t.h, pn.h, p),
    r: lerp(t.r, pn.r, p)
  }
}

// The shadow follows the surface as it grows, but stops at the settled panel — it never rides
// the spring's overshoot. The surface can overshoot invisibly (it's a clipped fill, and the
// content on top is statically positioned), but the shadow is a 34px-blurred box: measured, the
// bounce carries the rect ~5px past each edge, which sweeps the blur well past where the card
// lands and reads as a second object sliding out. Clamped, the shadow simply sits at panel size
// under a briefly-larger blob. See #1079.
const shadowProgress = (p: number) => Math.min(p, 1)

function insetForProgress(geo: Geo, p: number): string {
  return insetFor(rectForProgress(geo, p), geo.layerW, geo.layerH)
}

/** A morph rect as absolute-positioning styles — the shadow's first paint, before `render` drives it. */
function rectStyle(rect: Rect): React.CSSProperties {
  return { left: rect.x, top: rect.y, width: rect.w, height: rect.h, borderRadius: rect.r }
}

interface GooeyPopoverContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
  openHover: () => void
  scheduleClose: () => void
  triggerMode: TriggerMode
  manageFocus: boolean
  side: Side
  align: Align
  gap: number
  panelRadius: number
  gooStrength: number
  reduce: boolean
  gooId: string
  contentId: string
  progress: MotionValue<number>
  triggerRef: React.MutableRefObject<HTMLElement | null>
}

const GooeyPopoverContext = createContext<GooeyPopoverContextValue | null>(null)

function usePopoverContext(component: string) {
  const ctx = useContext(GooeyPopoverContext)
  if (!ctx) throw new Error(`${component} must be used within <GooeyPopover>`)
  return ctx
}

export interface GooeyPopoverProps {
  children: ReactNode
  /** Controlled open state. */
  open?: boolean
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /** How the popover is summoned. Default "click". */
  trigger?: TriggerMode
  /**
   * Move focus into the panel on open and back to the trigger on close. Default true; turn it off
   * for a popover the app opens on its own, which must not pull focus from what the user is doing.
   */
  manageFocus?: boolean
  /** Which side of the trigger the panel oozes out of. Default "bottom". */
  side?: Side
  /** Alignment along the trigger's edge. Default "center". */
  align?: Align
  /** Gap between trigger and panel, in px — the length of the gooey neck. Default 14. */
  sideOffset?: number
  /** Corner radius of the open panel, in px. Default 16. */
  panelRadius?: number
  /** Blur radius feeding the goo filter — higher melts more. Default 6 (calmer than beUI's 8). */
  gooStrength?: number
  className?: string
}

export function GooeyPopover({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  trigger = 'click',
  manageFocus = true,
  side = 'bottom',
  align = 'center',
  sideOffset = 14,
  panelRadius = 16,
  gooStrength = 6,
  className
}: GooeyPopoverProps) {
  const reduce = useReducedMotion() ?? false
  const gooId = useId().replace(/:/g, '')
  const contentId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Mounting already open renders settled rather than replaying the entrance, so a popover whose
  // host remounts (e.g. a header rebuilt on navigation) doesn't re-ooze.
  const progress = useMotionValue(defaultOpen || controlledOpen ? 1 : 0)

  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const controlled = controlledOpen !== undefined
  const open = controlled ? controlledOpen : internalOpen

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [controlled, onOpenChange]
  )

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const openHover = useCallback(() => {
    cancelClose()
    setOpen(true)
  }, [cancelClose, setOpen])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY)
  }, [cancelClose, setOpen])

  const toggle = useCallback(() => setOpen(!open), [setOpen, open])

  useEffect(() => () => cancelClose(), [cancelClose])

  useEffect(() => {
    const animation = animate(progress, open ? 1 : 0, reduce ? { duration: 0 } : GOO_SPRING)
    return () => animation.stop()
  }, [open, progress, reduce])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    // Trigger and panel share rootRef, so moving between them isn't "outside".
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    if (trigger === 'click') window.addEventListener('pointerdown', onPointer)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('pointerdown', onPointer)
    }
  }, [open, setOpen, trigger])

  const ctx = useMemo<GooeyPopoverContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      openHover,
      scheduleClose,
      triggerMode: trigger,
      manageFocus,
      side,
      align,
      gap: sideOffset,
      panelRadius,
      gooStrength,
      reduce,
      gooId,
      contentId,
      progress,
      triggerRef
    }),
    [
      open,
      setOpen,
      toggle,
      openHover,
      scheduleClose,
      trigger,
      manageFocus,
      side,
      align,
      sideOffset,
      panelRadius,
      gooStrength,
      reduce,
      gooId,
      contentId,
      progress
    ]
  )

  const hoverHandlers = trigger === 'hover' ? { onMouseEnter: openHover, onMouseLeave: scheduleClose } : {}

  return (
    <GooeyPopoverContext.Provider value={ctx}>
      <div ref={rootRef} className={cn('relative inline-flex isolate', className)} {...hoverHandlers}>
        {children}
      </div>
    </GooeyPopoverContext.Provider>
  )
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node)
      else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<T | null>).current = node
    }
  }
}

export interface GooeyPopoverTriggerProps {
  /** A single focusable element (e.g. a Button) that opens the popover. */
  children: ReactElement
}

export function GooeyPopoverTrigger({ children }: GooeyPopoverTriggerProps) {
  const ctx = usePopoverContext('GooeyPopoverTrigger')

  if (!isValidElement(children)) return children

  const child = children as ReactElement<Record<string, unknown>>
  const childProps = child.props
  const childRef = (childProps as { ref?: Ref<HTMLElement> }).ref

  const compose = (name: string, handler: () => void) => (event: { defaultPrevented?: boolean }) => {
    ;(childProps[name] as ((e: unknown) => void) | undefined)?.(event)
    if (!event.defaultPrevented) handler()
  }

  const handlers: Record<string, unknown> =
    ctx.triggerMode === 'hover'
      ? { onFocus: compose('onFocus', ctx.openHover), onBlur: compose('onBlur', ctx.scheduleClose) }
      : { onClick: compose('onClick', ctx.toggle) }

  return cloneElement(child, {
    ...handlers,
    ref: mergeRefs(childRef, (node: HTMLElement | null) => {
      ctx.triggerRef.current = node
    }),
    // Above the goo layer (z-[-1]) so the neck reads behind it.
    className: cn('relative z-0', childProps.className as string | undefined),
    'aria-haspopup': 'dialog',
    'aria-expanded': ctx.open,
    'aria-controls': ctx.open ? ctx.contentId : undefined,
    'data-state': ctx.open ? 'open' : 'closed'
  })
}

const ALIGN_ORIGIN: Record<Align, string> = { start: 'left', center: 'center', end: 'right' }

export interface GooeyPopoverContentProps {
  children: ReactNode
  className?: string
  /** Accessible name for the dialog (role="dialog"). */
  'aria-label'?: string
}

export function GooeyPopoverContent({ children, className, 'aria-label': ariaLabel }: GooeyPopoverContentProps) {
  const ctx = usePopoverContext('GooeyPopoverContent')
  const {
    side,
    align,
    gap,
    panelRadius,
    gooStrength,
    reduce,
    gooId,
    contentId,
    progress,
    triggerRef,
    open,
    triggerMode,
    manageFocus,
    openHover,
    scheduleClose
  } = ctx

  const measureRef = useRef<HTMLDivElement>(null)
  const shadowRef = useRef<HTMLDivElement>(null)
  const shadowRectRef = useRef<HTMLDivElement>(null)
  const gooBodyRef = useRef<HTMLDivElement>(null)
  const blobRef = useRef<HTMLDivElement>(null)
  const clipRef = useRef<HTMLDivElement>(null)
  const geoRef = useRef<Geo | null>(null)

  const [sizes, setSizes] = useState({ tW: 0, tH: 0, cW: 0, cH: 0 })
  // How far to nudge the panel horizontally so it stays inside the viewport (#1033).
  const [shiftX, setShiftX] = useState(0)

  useLayoutEffect(() => {
    const triggerNode = triggerRef.current
    const contentNode = measureRef.current
    if (!contentNode) return

    const measure = () => {
      const tW = triggerNode?.offsetWidth ?? 0
      const tH = triggerNode?.offsetHeight ?? 0
      const cW = contentNode.offsetWidth
      const cH = contentNode.offsetHeight
      setSizes((prev) => (prev.tW === tW && prev.tH === tH && prev.cW === cW && prev.cH === cH ? prev : { tW, tH, cW, cH }))
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(contentNode)
    if (triggerNode) observer.observe(triggerNode)
    return () => observer.disconnect()
  }, [triggerRef])

  // Recompute the viewport shift from the trigger's live screen position — only while open, so a
  // closed (clipped-to-trigger) panel costs nothing. Recomputes on open and on anything that can
  // move the trigger or resize the viewport (resize, and scroll in any ancestor — hence capture).
  // Keyed on measured sizes so it re-runs once the panel width is known.
  useLayoutEffect(() => {
    const triggerNode = triggerRef.current
    if (!open || !triggerNode || sizes.cW === 0) return

    const recompute = () => {
      const basePx = alignOffsetX(align, sizes.tW, sizes.cW)
      const next = clampShiftX(triggerNode.getBoundingClientRect().left, basePx, sizes.cW, window.innerWidth, VIEWPORT_PADDING)
      setShiftX((prev) => (Math.abs(prev - next) < 0.5 ? prev : next))
    }
    recompute()

    window.addEventListener('resize', recompute)
    window.addEventListener('scroll', recompute, true)
    return () => {
      window.removeEventListener('resize', recompute)
      window.removeEventListener('scroll', recompute, true)
    }
  }, [sizes, align, open, triggerRef])

  const geo = useMemo(
    () => buildGeo(sizes.tW, sizes.tH, sizes.cW, sizes.cH, side, align, gap, panelRadius, shiftX),
    [sizes, side, align, gap, panelRadius, shiftX]
  )

  // Morph the same clip on the goo body and the content, so the whole popover oozes as one
  // and the text reveals with it. The goo body also fades with progress, so a ghost/transparent
  // trigger shows no resting bg-popover pill behind it (it only appears as the panel oozes out).
  const render = useCallback((g: Geo | null, p: number) => {
    if (!g || g.layerW === 0) return
    const clip = insetForProgress(g, p)
    if (blobRef.current) blobRef.current.style.clipPath = clip
    if (clipRef.current) clipRef.current.style.clipPath = clip
    if (gooBodyRef.current) gooBodyRef.current.style.opacity = String(p)
    // The shadow's RECT grows with the surface, not just its opacity (#1079). Pinned to the
    // final panel rect it was casting a full 320×96 shadow while the blob was still a 70px
    // nub — a dark shape sitting out ahead of the card, which no opacity ramp can hide.
    const sp = shadowProgress(p)
    if (shadowRef.current) shadowRef.current.style.opacity = String(sp)
    if (shadowRectRef.current) {
      const rect = rectForProgress(g, sp)
      const s = shadowRectRef.current.style
      s.left = `${rect.x}px`
      s.top = `${rect.y}px`
      s.width = `${rect.w}px`
      s.height = `${rect.h}px`
      s.borderRadius = `${rect.r}px`
    }
  }, [])

  useLayoutEffect(() => {
    geoRef.current = geo
    render(geo, progress.get())
  }, [geo, progress, render])

  useMotionValueEvent(progress, 'change', (p) => render(geoRef.current, p))

  // Focus management (added atop the beUI base): move focus into the panel on open and return
  // it to the trigger on close. CLICK TRIGGER ONLY — a hover popover must never move focus:
  // returning focus to the trigger there re-fires its onFocus → openHover and reopens the panel
  // (and stealing focus on hover is wrong regardless). Guarded by prevOpen so an initial
  // `defaultOpen` mount doesn't steal focus on page load.
  const prevOpenRef = useRef(open)
  useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = open
    if (triggerMode !== 'click' || !manageFocus) return
    if (open && !wasOpen) measureRef.current?.focus({ preventScroll: true })
    else if (!open && wasOpen) triggerRef.current?.focus({ preventScroll: true })
  }, [open, triggerMode, manageFocus, triggerRef])

  const hoverHandlers = triggerMode === 'hover' ? { onMouseEnter: openHover, onMouseLeave: scheduleClose } : {}

  // When closed, mark the (still-mounted, measured) panel subtree `inert` so it's removed from
  // the a11y tree and can't be focused — otherwise a closed popover exposes a stray, duplicate
  // control (e.g. an "Undo" button) to assistive tech. Set the attribute imperatively: React 18
  // has no `inert` support (it landed in React 19), so a JSX `inert` prop is silently dropped.
  useLayoutEffect(() => {
    clipRef.current?.toggleAttribute('inert', !open)
  }, [open])

  return (
    <>
      {/* Goo filter: blur, sharpen the alpha back into solid shapes, then lay the crisp
          original on top so blobs merge with liquid edges. */}
      <svg aria-hidden width="0" height="0" className="pointer-events-none absolute">
        <title>Popover goo filter</title>
        <defs>
          <filter id={gooId} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation={gooStrength} result="blur" />
            <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10" result="goo" />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>
        </defs>
      </svg>

      {/* Panel shadow: a plain box-shadow on a rounded rect at the panel, behind everything. Kept
          off the goo-filtered element on purpose (see PANEL_SHADOW) so iOS doesn't clip it. Fades
          with progress; sized to the panel rect so the resting trigger casts none. */}
      <div
        ref={shadowRef}
        aria-hidden
        className="pointer-events-none absolute z-[-2]"
        style={{ left: geo.left, top: geo.top, width: geo.layerW, height: geo.layerH, opacity: progress.get() }}>
        <div
          ref={shadowRectRef}
          className="absolute"
          style={{ ...rectStyle(rectForProgress(geo, shadowProgress(progress.get()))), boxShadow: PANEL_SHADOW }}
        />
      </div>

      {/* Goo body: the morphing blob (clipped trigger → panel) through the metaball filter. It
          carries both the ooze animation and the panel surface, and fades with progress so a
          ghost/transparent trigger shows no resting bg-popover pill behind it. (No separate static
          trigger-anchor pill: the goo filter feathers a fringe just beyond its edge that would halo
          the trigger — the tradeoff is no persistent gooey neck at full-open, the ooze lives in the
          blob morph.) */}
      <div
        ref={gooBodyRef}
        aria-hidden
        className="pointer-events-none absolute z-[-1]"
        style={{
          left: geo.left,
          top: geo.top,
          width: geo.layerW,
          height: geo.layerH,
          opacity: progress.get(),
          filter: reduce ? undefined : `url(#${gooId})`
        }}>
        <div ref={blobRef} className="absolute inset-0 bg-popover" style={{ clipPath: insetForProgress(geo, progress.get()) }} />
      </div>

      {/* Content, clipped by the same morph. pointer-events-none so it never shadows the
          trigger; the open panel re-enables its own. */}
      <div className="pointer-events-none absolute z-10" style={{ left: geo.left, top: geo.top, width: geo.layerW, height: geo.layerH }}>
        <div
          ref={clipRef}
          className="absolute inset-0"
          style={{ clipPath: insetForProgress(geo, progress.get()), pointerEvents: open ? 'auto' : 'none' }}>
          <div
            ref={measureRef}
            id={contentId}
            role="dialog"
            aria-label={ariaLabel}
            tabIndex={-1}
            {...hoverHandlers}
            style={{
              position: 'absolute',
              left: geo.panel.x,
              top: geo.panel.y,
              transformOrigin: `${ALIGN_ORIGIN[align]} ${side === 'bottom' ? 'top' : 'bottom'}`
            }}
            // Transparent on purpose: the visible card surface is the morphing goo blob
            // behind this (also bg-popover). Elevation/border, if wanted, is a consumer
            // concern — the blob is the shape.
            className={cn('w-max max-w-[min(92vw,20rem)] p-4 text-popover-foreground outline-none', className)}>
            {children}
          </div>
        </div>
      </div>
    </>
  )
}
